import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/min'
import type { ContactMeOpenDetail, SupportedLocale } from '../types'

type NormalizePhoneOptions = {
  locale?: string
  context?: ContactMeOpenDetail['context']
}

const DEFAULT_COUNTRY_BY_LOCALE: Partial<Record<SupportedLocale, CountryCode>> = {
  sv: 'SE',
  es: 'ES',
  de: 'DE',
  fr: 'FR',
  nl: 'NL',
  fi: 'FI',
  no: 'NO',
  da: 'DK',
  pl: 'PL',
}

function toCountryCode(value: unknown): CountryCode | undefined {
  if (typeof value !== 'string') return undefined
  const normalized = value.trim().toUpperCase()
  return /^[A-Z]{2}$/.test(normalized) ? (normalized as CountryCode) : undefined
}

function getCountryHintFromContext(
  context?: ContactMeOpenDetail['context']
): CountryCode | undefined {
  if (!context || typeof context !== 'object') return undefined

  const keys = [
    'phoneCountry',
    'phoneCountryCode',
    'countryCode',
    'phone_country',
    'phone_country_code',
    'country_code',
  ] as const

  for (const key of keys) {
    const hit = toCountryCode((context as Record<string, unknown>)[key])
    if (hit) return hit
  }

  return undefined
}

function normalizeRawPhoneInput(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return ''
  return trimmed.replace(/^00/, '+')
}

export function normalizePhoneForSubmit(
  value: string,
  options: NormalizePhoneOptions = {}
): string {
  const normalizedInput = normalizeRawPhoneInput(value)
  if (!normalizedInput) return ''

  const defaultCountry =
    getCountryHintFromContext(options.context) ??
    (options.locale ? DEFAULT_COUNTRY_BY_LOCALE[options.locale as SupportedLocale] : undefined)

  const parsed = parsePhoneNumberFromString(normalizedInput, defaultCountry)
  if (parsed?.isValid()) {
    return parsed.number
  }

  return normalizedInput
}
